import { Button, Modal, Select, Table, message, Space } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import API from "../../Config/API";
import { useSelector } from "react-redux";
import TransactionRequestModal from "./TransactionRequestModal";

const TransactionApprovalScreen = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [selectedTransactionRows, setSelectedTransactionRows] = useState([]);
  const Auth = useSelector((state) => state.Auth.user);

  useEffect(() => {
    fetchAllDrivers();
    getAllDriverLedgers();
  }, []);

  useEffect(() => {
    getAllDriverLedgers();
  }, [selectedDriver]);


  const fetchAllDrivers = async () => {
    try {
      const response = await fetch(API.BASR_URL + API.GETALLDRIVERS);
      const data = await response.json();
      setDrivers(data?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const getAllDriverLedgers = async () => {
    try {
      setIsLoading(true);
      let url = `${API.BASR_URL}${API.GETALLTRANSACTIONREQUEST}?status=pending`;
      if (selectedDriver?.account_no) {
        url = `${API.BASR_URL}${API.GETALLTRANSACTIONREQUEST}?id=${selectedDriver?.account_no}&status=pending`;
      }
      const response = await fetch(url);
      const data = await response?.json();
      setTransactions(data?.data || []);
      setSelectedTransactionRows([]); // Reset selection
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setIsLoading(false);
    }
  };


  // Table row selection config
  const rowSelection = {
    selectedRowKeys: selectedTransactionRows,
    onChange: (selectedRowKeys) => setSelectedTransactionRows(selectedRowKeys),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      {
        key: "select-pending",
        text: "Select Pending",
        onSelect: () => {
          const pendingIds = transactions
            .filter((transaction) => transaction.status === "pending")
            .map((transaction) => transaction.id);
          setSelectedTransactionRows(pendingIds);
        },
      },
    ],
  };

  // Table columns
  const transactionColumns = [
    { title: "ID", dataIndex: "id", key: "id" },
    {
      title: "From Account",
      dataIndex: "fromName  ",
      key: "fromName",
      render: (_,record) => {
        return (
          <div>
            {record?.fromName} - {record?.ledgerFrom}
          </div>
        );
      },
    },
    {
      title: "To Account",
      dataIndex: "toName",
      key: "toName",
      render: (_,record) => {
        return (
          <div>
            {record?.toName} - {record?.ledgerTo}
          </div>
        );
      },
    },
    { title: "Amount", dataIndex: "amount", key: "amount" },
    { title: "Notes", dataIndex: "notes", key: "notes" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span
          style={{
            color:
              status === "pending"
                ? "#faad14"
                : status === "approved"
                ? "#52c41a"
                : "#ff4d4f",
          }}
        >
          {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
        </span>
      ),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (_,record) => {
        return <div>{record?.user?.name}</div>;
      },
    },

    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => new Date(text)?.toLocaleString(),
    },
    {
      title: "Approve",
      key: "action1",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleApproveTransaction(record)}
        >
          Approve
        </Button>
      ),
    },
    {
      title: "Delete",
      key: "action",
      render: (_, record) => (
        <Button
          type="danger"
          size="small"
          onClick={() => handleDeleteTransaction(record)}
        >
          Delete
        </Button>
      ),
    },
  ];

  // Single transaction approval
  const handleApproveTransaction = (transaction) => {
    setSelectedTransactionRows([transaction.id]); // Set selection

    Modal.confirm({
      title: "Confirm Transaction Approval",
      content: `Are you sure you want to approve transaction ID: ${transaction?.id} for $${transaction?.amount}?`,
      onOk: () => {
        handleSubmit({ transactions: [transaction] });
      },
    });
  };
  const handleDeleteTransaction = (transaction) => {
    Modal.confirm({
      title: "Confirm Transaction Deletion",
      content: `Are you sure you want to delete transaction ID: ${transaction?.id} for $${transaction?.amount}?`,
      onOk: () => {
        handleDelete(transaction?.id);
      },
    });
  };

  // Multiple transaction approvals
  const handleApproveSelectedTransactions = () => {
    if (selectedTransactionRows.length === 0) {
      message.warning("Please select at least one transaction to approve");
      return;
    }

    const selectedData = transactions.filter((transaction) =>
      selectedTransactionRows.includes(transaction.id)
    );

    Modal.confirm({
      title: "Confirm Multiple Transaction Approvals",
      content: `Are you sure you want to approve ${selectedTransactionRows.length} selected transaction(s)?`,
      onOk: () => {
        handleSubmit({ transactions: selectedData });
      },
    });
  };

  // Submit approval for transactions
  const handleSubmit = async (selectedData) => {
    try {
      setIsLoading(true);

      // Detect transaction request transactions
      const receiptCollections =
        selectedData?.transactions?.filter((txn) =>
          txn?.notes?.toLowerCase()?.includes("transaction request")
        ) || [];

      // If there are receipt collections, show confirmation modal
      if (receiptCollections.length > 0) {
        // Check if user has account number
        const userAccountNo = Auth?.account_no;

        // Show modal using component
        const modal = TransactionRequestModal({
          receiptCollections,
          userAccountNo,
          onApprove: (approverAccountNo) =>
            processApproval(selectedData, approverAccountNo),
        });
        modal.showModal();
      } else {
        // No receipt collections, proceed with normal approval
        await processApproval(selectedData, null);
      }
    } catch (error) {
      message.error(`Failed to approve transactions: ${error}`);
      console.error("Approval error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const processApproval = async (selectedData, approverAccountNo) => {
    try {
      setIsLoading(true);
      // Construct API payload
      const payload = {
        transactions: selectedData?.transactions?.map((txn) => ({
          transactionRequestId: txn?.id,
          userId: txn?.userId,
          amount: txn?.amount,
          ledgerFrom: txn?.ledgerFrom,
          // ledgerTo: txn?.ledgerTo,
          ledgerTo:
            txn?.notes?.toLowerCase()?.includes("transaction request") &&
            approverAccountNo &&
            txn?.reference?.toLowerCase?.() !== "bank"
              ? approverAccountNo
              : txn?.ledgerTo,
          type: txn?.type,
          note: `${txn?.fromName} - ${txn?.ledgerFrom} - ${txn?.notes}`,
          voucher_id: txn?.voucher_id,
          sale_id: txn?.sale_id,
          collection_id: txn?.collection_id,
          box_id: txn?.box_id,
          reference:
            txn?.notes?.toLowerCase()?.includes("transaction request") &&
            approverAccountNo
              ? txn?.id
              : txn?.reference,
          status: "approved",
          reason: txn?.reason,
          trans_date: new Date(),
        })),
      };

      const response = await axios.put(
        `${API.BASR_URL}${API.BOX_UPDATE_CLOSEPAYMENT}`,
        payload
      );

      if (response?.status === 200) {
        message.success("Transaction(s) approved successfully");
        getAllDriverLedgers(); // Refresh the list
        setSelectedTransactionRows([]); // Clear selection
      }
    } catch (error) {
      message.error(`Failed to approve transactions: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (selectedData) => {
    try {
      const url = `${API.BASR_URL}${
        API.DELETE_TRANSACTION_REQUEST + selectedData
      }`;

      const response = await axios.delete(url);
      if (response?.status === 200) {
        message.success("Transaction deleted successfully");
        getAllDriverLedgers();
      }
    } catch (error) {
      message.error(`Failed to delete transactions: ${error}`);
      console.error("Deletion error:", error);
    }
  };
  return (
    <div className="p-6">
      <div className="d-flex justify-content-between items-center mb-4">
        <h2 className="text-2xl font-bold">Transaction Approval</h2>
        <div className="d-flex gap-3">
          <Select
            placeholder="Select the driver"
            showSearch
            allowClear
            style={{ width: 200 }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children || "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent="No matching drivers found"
            onChange={(_, option) => setSelectedDriver(option?.data)}
          >
            {drivers?.map((driver) => (
              <Select.Option
                key={driver?.account_no}
                value={driver?.account_no}
                data={driver}
              >
                {driver?.account_name}
              </Select.Option>
            ))}
          </Select>
          <Button
            type="primary"
            onClick={handleApproveSelectedTransactions}
            disabled={selectedTransactionRows.length === 0}
          >
            Approve Selected Transactions
          </Button>
        </div>
      </div>

      {/* Transaction Table with selection controls */}
      <div className="mt-4">
        <div className="flex justify-between items-center mb-3">
          <Space>
            <span>{selectedTransactionRows.length} transactions selected</span>
          </Space>
        </div>

        <Table
          rowSelection={rowSelection}
          columns={transactionColumns}
          dataSource={transactions}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 10 }}
          bordered
          // scroll={{ x: "max-content" }}
        />
      </div>
    </div>
  );
};

export default TransactionApprovalScreen;

